import React, { useEffect, useState } from 'react'
import { Grid, InputLabel, Box, Typography, Accordion, AccordionSummary, AccordionDetails, Checkbox, InputAdornment, Radio, Tooltip, FormControl, Select, MenuItem } from '@mui/material';
import Layout from './Layout';
import TextField from '../../components/TextField';
import { CaretDown, Info } from '@phosphor-icons/react';
import Button from '../../components/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import { question as questionConstant } from './constants';
import { generateNextLabel, reorderLabels, sortOptions } from './lib';

const LayoutMcqMsq = ({ values, setValues, optionDetails, setOptionDetails, handleOption, question, answerLabels, handleAnswerLabel, setAnswerLabels }) => {

    const [labelType, setLabelType] = useState("number"); // 'number' or 'alphabet'
    const [lastLabel, setLastLabel] = useState(0); // Last used label for numbers or 'A'
    const [selectedStyle, setSelectedStyle] = useState("text");
    const [selectedTemplate, setSelectedTemplate] = useState("Side-by-Side");

    useEffect(() => {
        if (values && values?.length) {
            const style = values?.[0]?.value?.style;
            const template = values?.[0]?.value?.displayTemplate
            setSelectedStyle(style)
            setSelectedTemplate(template)
        }
    }, [values])

    useEffect(() => {
        if (selectedStyle && selectedTemplate) {
            setOptionDetails(prevQuestion => ({
                ...prevQuestion,
                style: selectedStyle,
                displayTemplate: selectedTemplate
            }));
        }

    }, [selectedStyle, selectedTemplate])

    const handleLabelTypeChange = (e) => {
        const type = e.target.value;

        if (values.length > 0) {
            alert("Cannot switch label type while options exist. Clear options first.");
            return;
        }

        setLabelType(type);
        setLastLabel(""); // Reset `lastLabel` for new type
    };

    const handleAddOption = () => {
        const nextLabel = generateNextLabel(labelType, lastLabel);
        let temp = {
            label: nextLabel,
            value: {
                style: optionDetails?.style,
                displayTemplate: optionDetails?.displayTemplate,
                text: optionDetails?.text,
                contentURL: optionDetails?.contentURL || '',
            },
        };

        setValues((prev) => sortOptions([...prev, temp]));
        setLastLabel(nextLabel); // Update the last used label
        setOptionDetails({ style: selectedStyle, displayTemplate: selectedTemplate, text: '', contentURL: '' }); // Clear option details
    };

    const handleDeleteOption = (index) => {
        const deletedOption = values[index];
        const newValues = values.filter((_, i) => i !== index);
        setLastLabel(newValues.length ? newValues[newValues.length - 1].label : ""); // Update last label
        const reorderedValues = reorderLabels(newValues, labelType); // Reassign labels
        setValues(reorderedValues);

        // Remove the deleted option's label from answerLabels
        const validLabels = reorderedValues.map(option => option.label);
        const updatedAnswerLabels = answerLabels.filter(label => validLabels.includes(label));
        setAnswerLabels(updatedAnswerLabels);
    }

    useEffect(() => {
        if (values.length > 0) {
            const firstLabel = values[0]?.label;

            if (!isNaN(firstLabel)) {
                setLabelType("123");
            }
            else if (/^[A-Z]+$/.test(firstLabel)) {
                setLabelType("AA");
            }
            else if (/^[a-z]+$/.test(firstLabel)) {
                setLabelType("aa");
            }
            setLastLabel(values[values.length - 1]?.label || "");
        } else {
            setLabelType("123");
            setLastLabel("");
        }
    }, [values]);

    const isValidOption = (option) => {
        if (!option?.style || !option?.displayTemplate) return false;

        // For text-only style, text is required
        if (option.style === 'text') {
            return !!option.text;
        }

        // For media styles, contentURL is required (text is optional)
        if (option.style === 'text-and-image' ||
            option.style === 'text-and-video' ||
            option.style === 'text-and-audio') {
            return !!option.contentURL;
        }

        return false;
    };

    return (
        <React.Fragment>
            <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography
                    align="left"
                    variant="h6"
                    color="primary.blue"
                    sx={{
                        margin: '0',
                        fontWeight: '700',
                        lineHeight: '1.5',
                    }}>Answer Options <span style={{ color: 'red' }}>*</span></Typography>
            </Grid>
            <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
            >
                <InputLabel style={styles.inputLabel}>Style</InputLabel>
                <FormControl fullWidth>
                    <Select
                        name="style"
                        value={selectedStyle || ''}
                        onChange={(e) => setSelectedStyle(e.target.value)}
                        placeholder="Style"
                        sx={{ height: '47px' }}
                        disabled={values?.length > 0}
                    >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {Object.keys(questionConstant.STYLES).map((key, i) => <MenuItem key={i} value={key}>{questionConstant.STYLES[key]}</MenuItem>)}
                    </Select>
                </FormControl>
            </Grid>
            <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
            >
                <InputLabel style={styles.inputLabel}>Display Template</InputLabel>
                <FormControl fullWidth>
                    <Select
                        name="displayTemplate"
                        value={selectedTemplate || ''}
                        onChange={(e) => setSelectedTemplate(e.target.value)}
                        placeholder="Display Template"
                        sx={{ height: '47px' }}
                        disabled={values?.length > 0}
                    >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {questionConstant.DISPLAY_TEMPLATE.map((item, index) => {
                            return <MenuItem key={index} value={item}>{item}</MenuItem>
                        })}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <Grid container alignItems="center" spacing={2}>
                    <Grid item sx={{ display: 'flex' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Checkbox
                                checked={labelType === "AA"}
                                onChange={handleLabelTypeChange}
                                value="AA"
                                disabled={values?.length > 0}
                            />
                            <Typography>(ABC)</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Checkbox
                                checked={labelType === "aa"}
                                onChange={handleLabelTypeChange}
                                value="aa"
                                disabled={values?.length > 0}
                            />
                            <Typography>(abc)</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Checkbox
                                checked={labelType === "123"}
                                onChange={handleLabelTypeChange}
                                value="123"
                                disabled={values?.length > 0}
                            />
                            <Typography>(123)</Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Grid>
            {
                values &&
                <Grid item xs={12}  >
                    {values.map((item, index) => {
                        const label = item?.label;
                        const text = item?.value?.text ? item?.value?.text : '';
                        const contentUrl = item?.value?.contentURL ? item?.value?.contentURL : '';
                        const contentType = item.value?.style;
                        const mediaType = questionConstant?.STYLES[contentType] || 'Media';
                        return <Accordion key={index}>
                            <AccordionSummary
                                expandIcon={<CaretDown />}
                                aria-controls="panel1-content"
                                id="panel1-header"
                            >
                                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between' }} >
                                    <Typography
                                        align="left"
                                        color="primary.blue"
                                        sx={{
                                            margin: '0',
                                            fontWeight: '700',
                                            lineHeight: '1.5',
                                        }}>
                                        {`${label})
                                               ${contentUrl ? `${text && text} (${mediaType})` : text}
                                            `}
                                    </Typography>
                                    <Grid >
                                        <DeleteIcon sx={{ color: 'var(--mui-palette-primary-button)', fontSize: '1.2rem' }} onClick={() => handleDeleteOption(index)} />
                                    </Grid>
                                </Grid>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Layout
                                    type="show"
                                    details={item?.value}
                                />
                            </AccordionDetails>
                        </Accordion>
                    })}
                </Grid>
            }
            <Grid item xs={12}>
                <Grid item xs={12} sm={6} md={4} lg={3} >
                    <InputLabel style={styles.inputLabel}>Label</InputLabel>
                    <TextField
                        name="label"
                        fullWidth
                        value={generateNextLabel(labelType, lastLabel) || ''}
                        onChange={(e) => handleOption(e)}
                        required
                        disabled
                        inputProps={{
                            readOnly: true, // Make it readonly since it's auto-generated
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment sx={{ cursor: "pointer" }} position="end">
                                    <Tooltip title="The label must be either number or alphabet in order (i.e only lower or only upper cases)">
                                        <Info />
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <Layout
                    details={optionDetails}
                    setDetails={setOptionDetails}
                    selectedStyle={selectedStyle}
                    selectedTemplate={selectedTemplate}
                    selected={true}
                    mcq={true}
                />
            </Grid>
            <Grid item xs={12}>
                <Box sx={{ mt: 4 }}>
                    <Button size='large' disabled={!isValidOption(optionDetails)} onClick={() => handleAddOption(optionDetails)} variant="contained" color="primary" placeholder={'Add Option'} />
                </Box>
            </Grid>

            {values.length > 0 && <>
                <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography
                        align="left"
                        variant="h6"
                        color="primary.blue"
                        sx={{
                            margin: '0',
                            fontWeight: '700',
                            lineHeight: '1.5',
                        }}>Correct Answer<span style={{ color: 'red' }}>*</span></Typography>
                </Grid>
                {values?.map((item, index) => {
                    return (
                        <Grid key={index} item xs={12} sm={6} md={4} lg={3}>
                            <TextField
                                name="label"
                                fullWidth
                                value={item?.label || ''}
                                disabled
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            {question?.type === "MCQ" ?
                                                <Radio checked={answerLabels?.includes(item?.label)}
                                                    onChange={handleAnswerLabel(index)}
                                                    size="medium"
                                                    sx={{ padding: 0 }}
                                                /> : <Checkbox
                                                    checked={answerLabels?.includes(item?.label)}
                                                    onChange={handleAnswerLabel(index)}
                                                    size="medium"
                                                    sx={{ padding: 0 }}
                                                />
                                            }
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    )
                })}
            </>}
        </React.Fragment>
    )
}

const styles = {
    label: {
        color: 'var(--mui-palette-common-black)',
        fontWeight: 'bold'
    },
    inputLabel: {
        color: 'var(--mui-palette-primary-darkGrey)',
        marginBottom: '5px',
        fontWeight: '600',
        fontSize: '14px'

    },
    gridContainer: {
        mt: 2,
    },
    suggestionBox: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        marginTop: '10px',
    },
    suggestionItem: {
        padding: '10px',
        border: '1px solid #ccc',
        borderRadius: '2px',
        cursor: 'pointer',
        boxShadow: '0 4px 8px var(--mui-palette-secondary-boxShadow1)',
        backgroundColor: 'var(--mui-palette-gray-lightGrey300)',
    },
    uploadBtn: {
        width: '50px',
        height: '50px',
        borderRadius: '5px',
    },
    hiddenInput: {
        display: 'none',
    },
    actionBtnItem: {
        marginLeft: '5px',
        marginRight: '5px',
        cursor: 'pointer'
    }
};
export default LayoutMcqMsq